/**
 * Request model for GET/DELETE requests
 */
export type RequestModel = {
  /** URL query parameters */
  params?: object;
  /** HTTP headers */
  headers?: object;
  /** AbortSignal for request cancellation */
  signal?: AbortSignal;
};

/**
 * Request model for POST/PUT/PATCH requests (includes body)
 */
export type RequestWithBodyModel = RequestModel & {
  /** Request body (object or FormData) */
  body?: object | FormData;
};

/**
 * useFetch hook provides a typed fetch wrapper with methods for HTTP requests.
 * Handles JSON/FormData body serialization, content-type headers, and error parsing.
 * Returns methods for GET, POST, PUT, PATCH, and DELETE requests.
 * 
 * @hook
 * @returns {Object} Fetch methods object
 * @returns {Function} returns.get - GET request method
 * @returns {Function} returns.post - POST request method
 * @returns {Function} returns.put - PUT request method
 * @returns {Function} returns.patch - PATCH request method
 * @returns {Function} returns.delete - DELETE request method
 * 
 * @example
 * ```tsx
 * const { get, post } = useFetch();
 * 
 * // GET request
 * const data = await get<ResponseType>('/api/users', {
 *   params: { page: 1 },
 *   headers: { Authorization: 'Bearer token' }
 * });
 * 
 * // POST request
 * const result = await post<ResponseType>('/api/users', {
 *   body: { name: 'John' },
 *   headers: { 'Content-Type': 'application/json' }
 * });
 * ```
 */
export const useFetch = () => {
  const handleFetch = async (
    url: string,
    request: any,
    signal?: AbortSignal,
  ) => {
    const requestUrl = request?.params ? `${url}${request.params}` : url;

    // Extract body and handle it separately
    let body: any = undefined;
    if (request?.body) {
      body = request.body instanceof FormData
        ? request.body
        : JSON.stringify(request.body);
    }

    // Build headers - prioritize request.headers, then add defaults
    const headers: Record<string, string> = {};
    
    // Add default content type if not FormData
    if (!(request?.body instanceof FormData)) {
      headers['Content-Type'] = 'application/json';
    }
    
    // Merge request headers (these will override defaults)
    if (request?.headers) {
      Object.assign(headers, request.headers);
    }

    // Build fetch options, excluding headers and body from request spread
    const { headers: _, body: __, params: ___, ...restRequest } = request;
    const fetchOptions: RequestInit = {
      ...restRequest,
      headers,
      signal,
    };
    
    if (body !== undefined) {
      fetchOptions.body = body;
    }

    return fetch(requestUrl, fetchOptions)
      .then((response) => {
        if (!response.ok) throw response;

        const contentType = response.headers.get('content-type');
        const contentDisposition = response.headers.get('content-disposition');

        const headers = response.headers;

        const result =
          contentType &&
          (contentType?.indexOf('application/json') !== -1 ||
            contentType?.indexOf('text/plain') !== -1)
            ? response.json()
            : contentDisposition?.indexOf('attachment') !== -1
            ? response.blob()
            : response;

        return result;
      })
      .catch(async (err) => {
        // Check if err is a Response object with json method
        if (err && typeof err.json === 'function') {
          try {
            const errResult = await err.json();
            throw errResult;
          } catch (jsonError) {
            // If json parsing fails, throw the original error
            throw { message: err.statusText || 'An error occurred', status: err.status };
          }
        } else {
          // If err is not a Response object, throw it as is or wrap it
          throw err instanceof Error ? { message: err.message } : { message: 'An error occurred', error: err };
        }
      });
  };

  return {
    get: async <T>(url: string, request?: RequestModel): Promise<T> => {
      return handleFetch(url, { ...request, method: 'get' });
    },
    post: async <T>(
      url: string,
      request?: RequestWithBodyModel,
    ): Promise<T> => {
      return handleFetch(url, { ...request, method: 'post' });
    },
    put: async <T>(url: string, request?: RequestWithBodyModel): Promise<T> => {
      return handleFetch(url, { ...request, method: 'put' });
    },
    patch: async <T>(
      url: string,
      request?: RequestWithBodyModel,
    ): Promise<T> => {
      return handleFetch(url, { ...request, method: 'patch' });
    },
    delete: async <T>(url: string, request?: RequestModel): Promise<T> => {
      return handleFetch(url, { ...request, method: 'delete' });
    },
  };
};
